/**
 * Typed endpoints for /coach/admin/clients.
 *
 * Transport, error class and status mapping come from `src/api/client.ts`
 * (ADR-0056). What is genuinely client-specific and stays here: the
 * endpoint shapes, the `detail` code → field map, and the operator-facing
 * messages below.
 */
import { AdminApiError, request } from '../api/client.js'
import type {
  ClientDetail,
  ClientListResponse,
  CreateClientBody,
  ListClientsParams,
  UpdateClientBody,
} from './types.js'

export const adminClientsApi = {
  /** Every client, newest first by default. Also feeds the client picker. */
  list(params: ListClientsParams = {}): Promise<ClientListResponse> {
    const qs = new URLSearchParams()
    if (params.search) qs.set('search', params.search)
    // Explicit undefined check: `false` is a real value here, so a truthiness
    // test would silently drop the "customers only" filter.
    if (params.isInternal !== undefined)
      qs.set('isInternal', String(params.isInternal))
    // Repeated param, matching the server's `list[UUID]` — `append`, not
    // `set`, or every id after the first is dropped.
    for (const clientId of params.clientIds ?? []) qs.append('clientId', clientId)
    if (params.hasJoinableCohort !== undefined)
      qs.set('hasJoinableCohort', String(params.hasJoinableCohort))
    if (params.sortField !== undefined) qs.set('sortField', params.sortField)
    if (params.sortDirection !== undefined)
      qs.set('sortDirection', params.sortDirection)
    if (params.limit !== undefined) qs.set('limit', String(params.limit))
    if (params.offset !== undefined) qs.set('offset', String(params.offset))
    const suffix = qs.size > 0 ? `?${qs.toString()}` : ''
    return request<ClientListResponse>(`/coach/admin/clients${suffix}`, {
      method: 'GET',
    })
  },
  get(clientId: string): Promise<ClientDetail> {
    return request<ClientDetail>(`/coach/admin/clients/${clientId}`, {
      method: 'GET',
    })
  },
  create(body: CreateClientBody): Promise<ClientDetail> {
    return request<ClientDetail>('/coach/admin/clients', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
  update(clientId: string, patch: UpdateClientBody): Promise<ClientDetail> {
    return request<ClientDetail>(`/coach/admin/clients/${clientId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },
  /** Soft delete. 409 `client_has_students` while any student remains. */
  remove(clientId: string): Promise<void> {
    return request<void>(`/coach/admin/clients/${clientId}`, {
      method: 'DELETE',
    })
  },
}

/**
 * Which FIELD each server error belongs to, so the message renders at that
 * field instead of in a summary below the submit button.
 */
export const CLIENT_ERROR_FIELD = {
  slug_already_exists: 'slug',
  slug_immutable_after_activation: 'slug',
} as const

export type ClientField =
  (typeof CLIENT_ERROR_FIELD)[keyof typeof CLIENT_ERROR_FIELD]

/** Map the server's `detail` code to an operator-readable message. */
export function messageForClientError(err: unknown): string {
  if (err instanceof AdminApiError) {
    switch (err.detail) {
      case 'slug_already_exists':
        return 'That slug is already taken — including by a deleted client, which keeps its slug.'
      case 'slug_immutable_after_activation':
        return 'The slug is locked: a student in this client has already activated.'
      case 'client_has_students':
        return 'This client still has students. Remove or move them first.'
      case 'client_not_found':
        return 'That client no longer exists.'
      case 'admin_required':
        return 'You do not have permission to manage clients.'
      default:
        return err.message
    }
  }
  return err instanceof Error ? err.message : 'Request failed'
}
